/**
 * generateInsuranceCSV — Insurance-grade CSV export (v2)
 *
 * Produces a fully-populated CSV with all 17 columns (empty string for missing
 * data) so that adjuster tools can import without schema changes.
 *
 * Column order matches the spec:
 *   Item #, Name, Category, Room, Quantity, Brand, Model, Serial, Condition,
 *   Purchase Date, Purchase Price, Est RCV, Est ACV, Depreciation %, Photos,
 *   Date Added, Notes
 *
 * Sort order: by room name (case-insensitive), then by item name within room.
 * Items with no room are sorted under an empty string room (appear first).
 */

import { calculateACV, type ItemCondition } from "./depreciation";
import type { Asset } from "@/hooks/useAssets";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Minimal location shape used by this module */
export interface AssetLocation {
  id: string;
  name: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const HEADER = [
  "Item #",
  "Name",
  "Category",
  "Room",
  "Quantity",
  "Brand",
  "Model",
  "Serial",
  "UPC",
  "ISBN",
  "Condition",
  "Purchase Date",
  "Purchase Price",
  "Est RCV",
  "Est ACV",
  "Depreciation %",
  "Photos",
  "Date Added",
  "Notes",
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Escape a single CSV field per RFC 4180:
 * - Wrap in double-quotes
 * - Escape internal double-quotes by doubling them
 */
export function escapeCSVField(value: string | number | null | undefined): string {
  const str = value == null ? "" : String(value);
  // Always quote — simplest safe approach
  return `"${str.replace(/"/g, '""')}"`;
}

/**
 * Derive the year portion from a purchase_date string (ISO date or year string)
 * or return null.
 *
 * Handles:
 *   "2022-03-15" → 2022
 *   "2022"       → 2022
 *   null/""      → null
 */
function extractPurchaseYear(purchaseDate: string | null | undefined): number | null {
  if (!purchaseDate) return null;
  const year = parseInt(purchaseDate.slice(0, 4), 10);
  return isNaN(year) ? null : year;
}

/**
 * Resolve the Room name for an asset.
 * Looks up the location from the asset's embedded `locations` relation.
 */
function resolveRoom(asset: Asset): string {
  return asset.locations?.name ?? "";
}

/**
 * Count photos attached to an asset.
 * Photos are asset_documents where document_type === "photo".
 */
function countPhotos(asset: Asset): number {
  if (!asset.asset_documents) return 0;
  return asset.asset_documents.filter((d) => d.document_type === "photo").length;
}

/**
 * Format a date string (or ISO datetime) as YYYY-MM-DD, or return "".
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  // created_at is a full ISO string; purchase_date may be YYYY or YYYY-MM-DD
  return dateStr.slice(0, 10);
}

/**
 * Resolve RCV for an asset.
 * RCV = purchase_price if present, else current_estimated_value.
 */
function resolveRCV(asset: Asset): number | null {
  return asset.purchase_price ?? asset.current_estimated_value ?? null;
}

/**
 * Build one CSV row for an asset, given its sequential item number.
 */
function buildRow(asset: Asset, itemNumber: number): string {
  const room = resolveRoom(asset);
  const rcv = resolveRCV(asset);
  const purchaseYear = extractPurchaseYear(asset.purchase_date);

  // ACV + depreciation — only calculable when we have RCV and a purchase year
  let acv: string = "";
  let depreciationPct: string = "";

  if (rcv != null && purchaseYear != null) {
    const quantity = asset.quantity ?? 1;
    const totalRCV = rcv * quantity;
    const condition = asset.condition as ItemCondition | undefined;
    const result = calculateACV({
      replacementCost: totalRCV,
      purchaseYear,
      category: asset.category,
      condition,
    });
    if (result) {
      acv = String(result.acv);
      depreciationPct = `${result.depreciationPercent}%`;
    }
  }

  // RCV: multiply by quantity for the exported figure
  const quantity = asset.quantity ?? 1;
  const rcvDisplay = rcv != null ? String(Math.round(rcv * quantity)) : "";

  const photos = countPhotos(asset);
  const dateAdded = formatDate(asset.created_at);
  const purchaseDate = formatDate(asset.purchase_date);
  const purchasePrice = asset.purchase_price != null
    ? String(Math.round(asset.purchase_price * quantity))
    : "";

  const fields: (string | number | null | undefined)[] = [
    String(itemNumber).padStart(3, "0"), // Item #
    asset.name,                          // Name
    asset.category,                      // Category
    room,                                // Room
    quantity,                            // Quantity
    asset.brand ?? "",                   // Brand
    asset.model ?? "",                   // Model
    asset.serial_number ?? "",           // Serial
    asset.upc ?? "",                     // UPC
    asset.isbn ?? "",                    // ISBN
    asset.condition ?? "",               // Condition
    purchaseDate,                        // Purchase Date
    purchasePrice,                       // Purchase Price
    rcvDisplay,                          // Est RCV
    acv,                                 // Est ACV
    depreciationPct,                     // Depreciation %
    photos,                              // Photos (count)
    dateAdded,                           // Date Added
    asset.notes ?? "",                   // Notes
  ];

  return fields.map(escapeCSVField).join(",");
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Generate an insurance-grade CSV string from the provided assets.
 *
 * @param assets - Array of Asset objects (with `locations` and `asset_documents` populated)
 * @returns CSV string with header row + one row per asset, sorted by room then name
 */
export function generateInsuranceCSV(assets: Asset[]): string {
  // Sort by room (ascending, case-insensitive), then by name within room
  const sorted = [...assets].sort((a, b) => {
    const roomA = resolveRoom(a).toLowerCase();
    const roomB = resolveRoom(b).toLowerCase();
    if (roomA !== roomB) return roomA.localeCompare(roomB);
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });

  const headerRow = HEADER.map(escapeCSVField).join(",");
  const dataRows = sorted.map((asset, idx) => buildRow(asset, idx + 1));

  return [headerRow, ...dataRows].join("\n");
}

/**
 * Trigger a browser download of the given CSV string.
 *
 * @param csv - CSV string to download
 * @param filename - Desired filename (defaults to dated manifest-inventory file)
 */
export function downloadCSV(
  csv: string,
  filename?: string,
): void {
  const today = new Date().toISOString().slice(0, 10);
  const name = filename ?? `manifest-inventory-${today}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();

  URL.revokeObjectURL(url);
}
